import {BaseComponent} from '../base/component.base.js';
import template from './table-field.template.html';
import styles from './table-field.styles.css';
import {t, tp, tpl} from '../../i18n/i18n.js';
import {isTableNumber, newRowId, normalizeTableRows} from '../../core/page/page.core.js';
import {uploadProblem} from '../../core/page/upload.core.js';
import {TABLE_ROW_ID_KEY, TableColumnType} from '../../core/vocabulary.js';

/** Track width per column type; rows share the template so cells line up. */
const COLUMN_TRACKS = {
    [TableColumnType.TEXT]: ['9rem', '1.4fr'],
    [TableColumnType.NUMBER]: ['5.5rem', '0.6fr'],
    [TableColumnType.SELECT]: ['8rem', '1fr'],
    [TableColumnType.DOCUMENT]: ['10rem', '1fr'],
};
const HANDLE_TRACK = '2rem';
const ACTIONS_TRACK = '2.75rem';
const SUMMARY_VALUES = 2;
const MENU_OFFSET = 4;
const VIEWPORT_MARGIN = 8;

/**
 * Repeatable, typed rows for a `table` template field. Holds the row array plus the files
 * chosen for document cells that are not uploaded yet; the page form reads both through
 * `rows` and `files` when it builds the request. The label is slotted in by the page form
 * (`slot="label"`) so section cards can toggle its required indicator. Help text is slotted in
 * too (`slot="help"`); while the host carries `aria-describedby` (the page form points it at
 * that help), the header toggle is described by the slotted help, because an id reference does
 * not cross the shadow boundary.
 *
 * Emits a bubbling `change` event on every edit that is not already a native `input` event,
 * and `asset-open-request` when a stored document is opened.
 */
export class TableFieldComponent extends BaseComponent {
    /** @type {import('../../shell/types/user.types.js').TemplateField | null} */
    #field = null;
    /** @type {import('../../shell/types/user.types.js').TableRow[]} */
    #rows = [];
    /** @type {Map<string, Map<string, File>>} row id → column key → pending file */
    #files = new Map();
    /** Rows opened on narrow screens, where each row is a collapsed card. */
    #expanded = new Set();
    #initialized = false;
    #menuRowId = null;
    #draggedId = null;
    /**
     * The row deleted last, with what it held, while "Undo" can still put it back. Adding,
     * duplicating or moving a row, or replacing the rows, ends that chance.
     * @type {{row: Object, index: number, files: Map<string, File> | undefined, expanded: boolean} | null}
     */
    #lastDeleted = null;

    #onLocaleChange = () => this.#render();

    #onDocumentClick = (event) => {
        if (!this.#menuRowId) return;
        const menu = this.$('#row-menu');
        const path = event.composedPath();
        const ownButton = (node) => node?.classList?.contains('btn-more') && node.getRootNode() === this.shadowRoot;
        if (path.includes(menu) || path.some(ownButton)) return;
        this.#closeMenu();
    };

    #onKeydown = (event) => {
        if (event.key === 'Escape' && this.#menuRowId) {
            const rowId = this.#menuRowId;
            this.#closeMenu();
            this.#rowElement(rowId).querySelector('.btn-more').focus();
        }
    };

    #onViewportChange = () => this.#closeMenu();

    static get observedAttributes() {
        return ['aria-describedby'];
    }

    attributeChangedCallback() {
        this.#syncDescription();
    }

    connectedCallback() {
        if (!this.#initialized) {
            this.#initialized = true;
            this.loadTemplate(template, styles);
            this.#syncDescription();
            this.$('#table-header').addEventListener('click', () => this.#toggleCollapsed());
            this.$('#btn-add-row').addEventListener('click', () => this.#addRow());
            this.$('#btn-undo').addEventListener('click', () => this.#undoDelete());
            this.$('#row-menu').addEventListener('click', (event) => this.#onMenuClick(event));
            // Scroll events do not leave the shadow root, so the page-level listener misses these.
            this.$('#table-scroll').addEventListener('scroll', this.#onViewportChange);
            this.#render();
        }
        document.addEventListener('locale-change', this.#onLocaleChange);
        document.addEventListener('click', this.#onDocumentClick);
        document.addEventListener('keydown', this.#onKeydown);
        window.addEventListener('resize', this.#onViewportChange);
        window.addEventListener('scroll', this.#onViewportChange, true);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('locale-change', this.#onLocaleChange);
        document.removeEventListener('click', this.#onDocumentClick);
        document.removeEventListener('keydown', this.#onKeydown);
        window.removeEventListener('resize', this.#onViewportChange);
        window.removeEventListener('scroll', this.#onViewportChange, true);
    }

    /** Describes the header toggle with the slotted help while the host is described. */
    #syncDescription() {
        if (!this.#initialized) return;
        const header = this.$('#table-header');
        if (this.hasAttribute('aria-describedby')) {
            header.setAttribute('aria-describedby', 'table-help');
        } else {
            header.removeAttribute('aria-describedby');
        }
    }

    get name() {
        return this.getAttribute('name') || '';
    }

    set name(value) {
        this.setAttribute('name', value);
    }

    /** @param {import('../../shell/types/user.types.js').TemplateField} value */
    set field(value) {
        this.#field = value;
        this.#rows = normalizeTableRows(this.#columns(), this.#rows);
        this.#render();
    }

    get field() {
        return this.#field;
    }

    /**
     * Replaces the rows, for example with stored page data. Rows without an `_id` get one.
     * Pending files are dropped.
     * @param {unknown} value
     */
    set rows(value) {
        this.#rows = normalizeTableRows(this.#columns(), value);
        this.#files = new Map();
        this.#expanded = new Set();
        this.#lastDeleted = null;
        this.#render();
    }

    /** @returns {import('../../shell/types/user.types.js').TableRow[]} */
    get rows() {
        return this.#rows.map((row) => ({...row}));
    }

    /** @returns {import('../../shell/types/user.types.js').PendingTableFile[]} */
    get files() {
        const pending = [];
        for (const row of this.#rows) {
            for (const [columnKey, file] of this.#files.get(row[TABLE_ROW_ID_KEY]) ?? []) {
                pending.push({rowId: row[TABLE_ROW_ID_KEY], columnKey, file});
            }
        }
        return pending;
    }

    /**
     * First cell that blocks saving, in row then column order: a blank cell in a required
     * column (a pending file fills a document cell) or a number cell holding something that is
     * not a number.
     * @returns {{rowId: string, rowIndex: number, columnKey: string, columnLabel: string, reason: 'required' | 'number'} | null}
     */
    firstInvalidCell() {
        for (const [rowIndex, row] of this.#rows.entries()) {
            for (const column of this.#columns()) {
                const cell = {rowId: row[TABLE_ROW_ID_KEY], rowIndex, columnKey: column.key, columnLabel: column.label};
                if (column.required && !this.#displayValue(row, column)) return {...cell, reason: 'required'};
                if (column.type === TableColumnType.NUMBER && !isTableNumber(row[column.key])) return {...cell, reason: 'number'};
            }
        }
        return null;
    }

    /**
     * Opens the row on narrow screens and focuses the control of one cell.
     * @param {string} rowId
     * @param {string} columnKey
     */
    focusCell(rowId, columnKey) {
        if (!this.#expanded.has(rowId)) this.#toggleRow(rowId);
        this.#rowElement(rowId).querySelector(`.cell[data-column-key="${CSS.escape(columnKey)}"] .cell-control`).focus();
    }

    /** Moves focus into the control: the first cell when there are rows, else "Add row". */
    focus() {
        const target = this.$('.table-row:not(.table-head) .cell-control') || this.$('#btn-add-row');
        target.focus();
    }

    #columns() {
        return this.#field?.columns ?? [];
    }

    #maxRows() {
        const max = Number(this.#field?.maxRows);
        return Number.isInteger(max) && max > 0 ? max : Infinity;
    }

    #atMax() {
        return this.#rows.length >= this.#maxRows();
    }

    #changed() {
        this.dispatchEvent(new Event('change', {bubbles: true}));
    }

    // ---- rendering ---------------------------------------------------------------------

    #render() {
        if (!this.#initialized) return;
        this.#closeMenu();
        const columns = this.#columns();
        const grid = this.$('#table-grid');
        const tracks = columns.map((column) => COLUMN_TRACKS[column.type] ?? COLUMN_TRACKS[TableColumnType.TEXT]);
        grid.style.setProperty('--table-template',
            [HANDLE_TRACK, ...tracks.map(([min, max]) => `minmax(${min}, ${max})`), ACTIONS_TRACK].join(' '));
        grid.style.setProperty('--table-min-width',
            `calc(${[HANDLE_TRACK, ...tracks.map(([min]) => min), ACTIONS_TRACK].join(' + ')} + ${columns.length + 1} * var(--table-gap))`);

        grid.replaceChildren(this.#createHeadRow(columns), ...this.#rows.map((row, index) => this.#createRow(row, index)));
        grid.hidden = this.#rows.length === 0;

        const empty = this.$('#table-empty');
        empty.hidden = this.#rows.length > 0;
        empty.textContent = t('tableField.empty');

        this.$('#add-row-label').textContent = t('tableField.addRow');
        this.#syncCounts();
        this.#syncUndo();

        const menuLabels = {
            up: 'tableField.moveUp',
            down: 'tableField.moveDown',
            duplicate: 'tableField.duplicate',
            delete: 'tableField.deleteRow',
        };
        this.$$('#row-menu .menu-item').forEach((item) => {
            item.querySelector('span').textContent = t(menuLabels[item.dataset.action]);
        });
    }

    /** Row count in the header plus the "Add row" limit, which change without a full render. */
    #syncCounts() {
        this.$('#row-count').textContent = tp('tableField.rowCount', this.#rows.length);
        const atMax = this.#atMax();
        this.$('#btn-add-row').disabled = atMax;
        const hint = this.$('#max-rows-hint');
        hint.hidden = !atMax;
        hint.textContent = atMax ? tpl('tableField.maxRowsReached', {n: this.#maxRows()}) : '';
    }

    #createHeadRow(columns) {
        const head = document.createElement('div');
        head.className = 'table-row table-head';
        head.setAttribute('role', 'row');
        const handleSpacer = document.createElement('div');
        handleSpacer.className = 'head-spacer';
        handleSpacer.setAttribute('role', 'none');
        handleSpacer.setAttribute('aria-hidden', 'true');
        head.appendChild(handleSpacer);
        for (const column of columns) {
            const cell = document.createElement('div');
            cell.className = 'head-cell';
            cell.setAttribute('role', 'columnheader');
            cell.textContent = column.label;
            if (column.required) cell.appendChild(this.#requiredMark());
            head.appendChild(cell);
        }
        const actions = document.createElement('div');
        actions.className = 'head-actions';
        actions.setAttribute('role', 'columnheader');
        const actionsLabel = document.createElement('span');
        actionsLabel.className = 'sr-only';
        actionsLabel.textContent = t('tableField.actionsColumn');
        actions.appendChild(actionsLabel);
        head.appendChild(actions);
        return head;
    }

    #requiredMark() {
        const mark = document.createElement('span');
        mark.className = 'required-mark';
        mark.setAttribute('aria-hidden', 'true');
        mark.textContent = ' *';
        return mark;
    }

    #createRow(row, index) {
        const element = document.createElement('div');
        element.className = 'table-row';
        element.setAttribute('role', 'row');
        element.dataset.rowId = row[TABLE_ROW_ID_KEY];
        element.classList.toggle('is-expanded', this.#expanded.has(row[TABLE_ROW_ID_KEY]));
        element.addEventListener('dragstart', (event) => this.#onDragStart(event, row[TABLE_ROW_ID_KEY]));
        element.addEventListener('dragover', (event) => this.#onDragOver(event, element));
        element.addEventListener('drop', (event) => this.#onDrop(event, element));
        element.addEventListener('dragend', () => this.#onDragEnd());

        const handle = document.createElement('button');
        handle.type = 'button';
        handle.className = 'row-handle';
        handle.tabIndex = -1;
        handle.title = t('tableField.dragHandle');
        handle.setAttribute('aria-hidden', 'true');
        const grip = document.createElement('i');
        grip.className = 'fa-solid fa-grip-vertical';
        handle.appendChild(grip);
        handle.addEventListener('pointerdown', () => {
            element.draggable = true;
        });
        handle.addEventListener('pointerup', () => {
            if (!this.#draggedId) element.draggable = false;
        });
        element.appendChild(handle);

        const summary = document.createElement('button');
        summary.type = 'button';
        summary.className = 'row-summary';
        summary.setAttribute('aria-expanded', String(this.#expanded.has(row[TABLE_ROW_ID_KEY])));
        const summaryTitle = document.createElement('span');
        summaryTitle.className = 'row-summary-title';
        const summaryMeta = document.createElement('span');
        summaryMeta.className = 'row-summary-meta';
        const chevron = document.createElement('i');
        chevron.className = 'fa-solid fa-chevron-down row-summary-chevron';
        chevron.setAttribute('aria-hidden', 'true');
        summary.append(summaryTitle, summaryMeta, chevron);
        summary.addEventListener('click', () => this.#toggleRow(row[TABLE_ROW_ID_KEY]));
        element.appendChild(summary);

        const cells = document.createElement('div');
        cells.className = 'row-cells';
        cells.setAttribute('role', 'none');
        for (const column of this.#columns()) {
            cells.appendChild(this.#createCell(row, index, column));
        }
        element.appendChild(cells);

        const actions = document.createElement('div');
        actions.className = 'row-actions';
        actions.setAttribute('role', 'cell');
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'btn-more';
        more.setAttribute('aria-haspopup', 'menu');
        more.setAttribute('aria-expanded', 'false');
        more.title = t('tableField.rowActions');
        const dots = document.createElement('i');
        dots.className = 'fa-solid fa-ellipsis';
        dots.setAttribute('aria-hidden', 'true');
        more.appendChild(dots);
        more.addEventListener('click', () => this.#toggleMenu(row[TABLE_ROW_ID_KEY], more));
        actions.appendChild(more);
        element.appendChild(actions);

        this.#syncSummary(element, row, index);
        return element;
    }

    #createCell(row, index, column) {
        const cell = document.createElement('div');
        cell.className = `cell cell--${column.type}`;
        cell.setAttribute('role', 'cell');
        cell.dataset.columnKey = column.key;

        const label = document.createElement('span');
        label.className = 'cell-label';
        label.textContent = column.label;
        if (column.required) label.appendChild(this.#requiredMark());
        cell.appendChild(label);

        if (column.type === TableColumnType.DOCUMENT) {
            cell.appendChild(this.#createDocumentCell(row, column));
            return cell;
        }

        let control;
        let error = null;
        if (column.type === TableColumnType.SELECT) {
            control = document.createElement('select');
            const options = ['', ...(column.options ?? [])];
            if (!options.includes(row[column.key])) options.push(row[column.key]);
            for (const option of options) {
                const element = document.createElement('option');
                element.value = option;
                element.textContent = option;
                control.appendChild(element);
            }
            control.value = row[column.key];
            control.addEventListener('change', () => {
                this.#setCell(row[TABLE_ROW_ID_KEY], column.key, control.value);
                this.#changed();
            });
        } else if (column.type === TableColumnType.NUMBER && !isTableNumber(row[column.key])) {
            ({control, error} = this.#createInvalidNumberInput(row, column));
        } else {
            control = document.createElement('input');
            control.type = column.type === TableColumnType.NUMBER ? 'number' : 'text';
            if (column.type === TableColumnType.NUMBER) control.step = 'any';
            control.value = row[column.key];
            control.addEventListener('input', () => this.#setCell(row[TABLE_ROW_ID_KEY], column.key, control.value));
        }
        control.className = 'cell-control';
        control.setAttribute('aria-label', `${column.label}, ${tpl('tableField.emptyRow', {n: index + 1})}`);
        if (column.required) control.setAttribute('aria-required', 'true');
        cell.appendChild(control);
        if (error) cell.appendChild(error);
        return cell;
    }

    /**
     * A number cell whose stored value is not a number. A number input would show it as
     * empty while the value is still sent, so it stays a text input marked invalid, with a
     * message, until the value is a number or blank.
     */
    #createInvalidNumberInput(row, column) {
        const control = document.createElement('input');
        control.type = 'text';
        control.inputMode = 'decimal';
        control.value = row[column.key];
        const error = document.createElement('small');
        error.className = 'cell-error';
        error.id = `error-${row[TABLE_ROW_ID_KEY]}-${column.key}`;
        error.textContent = t('tableField.invalidNumber');
        control.setAttribute('aria-describedby', error.id);
        const sync = () => {
            const valid = isTableNumber(control.value);
            control.setAttribute('aria-invalid', String(!valid));
            error.hidden = valid;
        };
        sync();
        control.addEventListener('input', () => {
            this.#setCell(row[TABLE_ROW_ID_KEY], column.key, control.value);
            sync();
        });
        return {control, error};
    }

    #createDocumentCell(row, column) {
        const box = document.createElement('div');
        box.className = 'doc-cell';
        const pending = this.#files.get(row[TABLE_ROW_ID_KEY])?.get(column.key) ?? null;
        const stored = row[column.key];

        if (pending) {
            const name = document.createElement('span');
            name.className = 'doc-name';
            name.title = pending.name;
            const icon = document.createElement('i');
            icon.className = 'fa-regular fa-file';
            icon.setAttribute('aria-hidden', 'true');
            name.append(icon, document.createTextNode(` ${pending.name}`));
            box.appendChild(name);
        } else if (stored) {
            const link = document.createElement('button');
            link.type = 'button';
            link.className = 'doc-link';
            link.title = stored;
            const icon = document.createElement('i');
            icon.className = 'fa-solid fa-download';
            icon.setAttribute('aria-hidden', 'true');
            link.append(icon, document.createTextNode(` ${column.label}`));
            link.addEventListener('click', () => this.emit('asset-open-request', {path: stored, filename: stored}));
            box.appendChild(link);
        }

        const filled = Boolean(pending || stored);
        const upload = document.createElement('label');
        upload.className = filled ? 'doc-replace icon-btn' : 'doc-upload';
        upload.title = filled ? t('tableField.replaceFile') : t('tableField.uploadFile');
        const uploadIcon = document.createElement('i');
        uploadIcon.className = filled ? 'fa-solid fa-arrow-up-from-bracket' : 'fa-solid fa-cloud-arrow-up';
        uploadIcon.setAttribute('aria-hidden', 'true');
        upload.appendChild(uploadIcon);
        if (!filled) upload.appendChild(document.createTextNode(` ${t('tableField.uploadFile')}`));
        const input = document.createElement('input');
        input.type = 'file';
        input.className = 'file-input-hidden cell-control';
        input.setAttribute('aria-label', `${upload.title}: ${column.label}`);
        input.addEventListener('change', () => {
            const file = input.files[0];
            if (!file) return;
            const problem = uploadProblem(file, {image: false});
            this.#showUploadError(problem ? tpl(problem.key, problem.vars) : '');
            if (problem) {
                input.value = '';
                return;
            }
            this.#setFile(row[TABLE_ROW_ID_KEY], column.key, file);
            this.#rerenderRow(row[TABLE_ROW_ID_KEY], '.doc-cell .doc-replace input', column.key);
            this.#changed();
        });
        upload.appendChild(input);
        box.appendChild(upload);

        if (filled) {
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'doc-remove icon-btn';
            remove.title = t('tableField.removeFile');
            remove.setAttribute('aria-label', `${t('tableField.removeFile')}: ${column.label}`);
            const removeIcon = document.createElement('i');
            removeIcon.className = 'fa-solid fa-xmark';
            removeIcon.setAttribute('aria-hidden', 'true');
            remove.appendChild(removeIcon);
            remove.addEventListener('click', () => {
                this.#files.get(row[TABLE_ROW_ID_KEY])?.delete(column.key);
                this.#setCell(row[TABLE_ROW_ID_KEY], column.key, '');
                this.#rerenderRow(row[TABLE_ROW_ID_KEY], '.doc-cell .doc-upload input', column.key);
                this.#changed();
            });
            box.appendChild(remove);
        }
        return box;
    }

    /** Mobile summary: first column (or "Row n") plus the first two other non-empty values. */
    #syncSummary(element, row, index) {
        const [first, ...others] = this.#columns().map((column) => this.#displayValue(row, column));
        const title = first || tpl('tableField.emptyRow', {n: index + 1});
        element.querySelector('.row-summary-title').textContent = title;
        element.querySelector('.btn-more').setAttribute('aria-label', `${t('tableField.rowActions')}: ${title}`);
        element.querySelector('.row-summary-meta').textContent = others.filter(Boolean).slice(0, SUMMARY_VALUES).join(' · ');
    }

    #displayValue(row, column) {
        if (column.type === TableColumnType.DOCUMENT) {
            const pending = this.#files.get(row[TABLE_ROW_ID_KEY])?.get(column.key);
            if (pending) return pending.name;
            return row[column.key] ? column.label : '';
        }
        return row[column.key].trim();
    }

    /** Replaces one row's DOM after a document change and restores focus to the cell. */
    #rerenderRow(rowId, focusSelector, columnKey) {
        const index = this.#indexOf(rowId);
        const next = this.#createRow(this.#rows[index], index);
        this.#rowElement(rowId).replaceWith(next);
        next.querySelector(`.cell[data-column-key="${columnKey}"] ${focusSelector}`).focus();
    }

    // ---- state changes -----------------------------------------------------------------

    #indexOf(rowId) {
        return this.#rows.findIndex((row) => row[TABLE_ROW_ID_KEY] === rowId);
    }

    #rowElement(rowId) {
        return this.$(`.table-row[data-row-id="${CSS.escape(rowId)}"]`);
    }

    #setCell(rowId, columnKey, value) {
        const index = this.#indexOf(rowId);
        this.#rows[index] = {...this.#rows[index], [columnKey]: value};
        this.#syncSummary(this.#rowElement(rowId), this.#rows[index], index);
    }

    #setFile(rowId, columnKey, file) {
        if (!this.#files.has(rowId)) this.#files.set(rowId, new Map());
        this.#files.get(rowId).set(columnKey, file);
        this.#syncSummary(this.#rowElement(rowId), this.#rows[this.#indexOf(rowId)], this.#indexOf(rowId));
    }

    #blankRow() {
        const row = {[TABLE_ROW_ID_KEY]: newRowId(this.#rows.map((existing) => existing[TABLE_ROW_ID_KEY]))};
        for (const column of this.#columns()) row[column.key] = '';
        return row;
    }

    /**
     * Says why a chosen document was refused, or clears that message.
     * @param {string} message
     */
    #showUploadError(message) {
        const error = this.$('#upload-error');
        error.hidden = !message;
        error.textContent = message;
    }

    /** Shows "Row n deleted" with its undo button while a deleted row can be restored. */
    #syncUndo() {
        const deleted = this.#lastDeleted;
        this.$('#row-undo-text').textContent = deleted ? tpl('tableField.rowDeleted', {n: deleted.index + 1}) : '';
        this.$('#btn-undo-label').textContent = t('tableField.undo');
        this.$('#btn-undo').hidden = !deleted;
    }

    /** Puts the row deleted last back in its place, with its pending files. */
    #undoDelete() {
        const deleted = this.#lastDeleted;
        if (!deleted) return;
        this.#lastDeleted = null;
        const rowId = deleted.row[TABLE_ROW_ID_KEY];
        this.#rows.splice(Math.min(deleted.index, this.#rows.length), 0, deleted.row);
        if (deleted.files) this.#files.set(rowId, deleted.files);
        if (deleted.expanded) this.#expanded.add(rowId);
        this.#render();
        this.#rowElement(rowId).querySelector('.btn-more').focus();
        this.#changed();
    }

    #addRow() {
        this.#lastDeleted = null;
        const row = this.#blankRow();
        this.#rows.push(row);
        this.#expanded.add(row[TABLE_ROW_ID_KEY]);
        this.#render();
        const element = this.#rowElement(row[TABLE_ROW_ID_KEY]);
        element.scrollIntoView({block: 'nearest'});
        element.querySelector('.cell-control').focus();
        this.#changed();
    }

    #duplicateRow(rowId) {
        this.#lastDeleted = null;
        const index = this.#indexOf(rowId);
        const copy = {...this.#rows[index], [TABLE_ROW_ID_KEY]: newRowId(this.#rows.map((row) => row[TABLE_ROW_ID_KEY]))};
        this.#rows.splice(index + 1, 0, copy);
        const files = this.#files.get(rowId);
        if (files) this.#files.set(copy[TABLE_ROW_ID_KEY], new Map(files));
        this.#expanded.add(copy[TABLE_ROW_ID_KEY]);
        this.#render();
        this.#rowElement(copy[TABLE_ROW_ID_KEY]).querySelector('.btn-more').focus();
        this.#changed();
    }

    #deleteRow(rowId) {
        const index = this.#indexOf(rowId);
        this.#lastDeleted = {row: this.#rows[index], index, files: this.#files.get(rowId), expanded: this.#expanded.has(rowId)};
        this.#rows.splice(index, 1);
        this.#files.delete(rowId);
        this.#expanded.delete(rowId);
        this.#render();
        const neighbour = this.#rows[Math.min(index, this.#rows.length - 1)];
        const target = neighbour ? this.#rowElement(neighbour[TABLE_ROW_ID_KEY]).querySelector('.btn-more') : this.$('#btn-add-row');
        target.focus();
        this.#changed();
    }

    #moveRow(rowId, targetIndex) {
        const index = this.#indexOf(rowId);
        if (targetIndex === index) return;
        this.#lastDeleted = null;
        const [row] = this.#rows.splice(index, 1);
        this.#rows.splice(targetIndex, 0, row);
        this.#render();
        this.#changed();
    }

    #toggleRow(rowId) {
        const element = this.#rowElement(rowId);
        const expanded = !this.#expanded.has(rowId);
        if (expanded) this.#expanded.add(rowId);
        else this.#expanded.delete(rowId);
        element.classList.toggle('is-expanded', expanded);
        element.querySelector('.row-summary').setAttribute('aria-expanded', String(expanded));
    }

    #toggleCollapsed() {
        const header = this.$('#table-header');
        const expanded = header.getAttribute('aria-expanded') !== 'true';
        header.setAttribute('aria-expanded', String(expanded));
        this.$('#table-body').hidden = !expanded;
        if (!expanded) this.#closeMenu();
    }

    // ---- row menu ----------------------------------------------------------------------

    #toggleMenu(rowId, button) {
        if (this.#menuRowId === rowId) {
            this.#closeMenu();
            return;
        }
        this.#closeMenu();
        this.#menuRowId = rowId;
        const index = this.#indexOf(rowId);
        const menu = this.$('#row-menu');
        menu.querySelector('[data-action="up"]').disabled = index === 0;
        menu.querySelector('[data-action="down"]').disabled = index === this.#rows.length - 1;
        menu.querySelector('[data-action="duplicate"]').disabled = this.#atMax();
        button.setAttribute('aria-expanded', 'true');
        const rect = button.getBoundingClientRect();
        menu.style.right = `${Math.max(document.documentElement.clientWidth - rect.right, VIEWPORT_MARGIN)}px`;
        menu.hidden = false;
        // Measured once visible; a menu that would run past the viewport opens above its button.
        const below = rect.bottom + MENU_OFFSET;
        const height = menu.offsetHeight;
        const top = below + height > window.innerHeight
            ? Math.max(rect.top - MENU_OFFSET - height, VIEWPORT_MARGIN)
            : below;
        menu.style.top = `${top}px`;
        menu.querySelector('.menu-item:not(:disabled)').focus();
    }

    #closeMenu() {
        this.#menuRowId = null;
        this.$('#row-menu').hidden = true;
        this.$$('.btn-more').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    }

    #onMenuClick(event) {
        const item = event.target.closest('.menu-item');
        if (!item || item.disabled) return;
        const rowId = this.#menuRowId;
        this.#closeMenu();
        const index = this.#indexOf(rowId);
        switch (item.dataset.action) {
            case 'up':
            case 'down':
                this.#moveRow(rowId, item.dataset.action === 'up' ? index - 1 : index + 1);
                this.#rowElement(rowId).querySelector('.btn-more').focus();
                break;
            case 'duplicate':
                this.#duplicateRow(rowId);
                break;
            default:
                this.#deleteRow(rowId);
        }
    }

    // ---- drag and drop (pointer enhancement; the row menu is the keyboard control) -------

    #onDragStart(event, rowId) {
        if (!event.currentTarget.draggable) {
            event.preventDefault();
            return;
        }
        this.#draggedId = rowId;
        event.currentTarget.classList.add('is-dragging');
        event.dataTransfer?.setData('text/plain', rowId);
        if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
    }

    #dropsAfter(event, element) {
        const rect = element.getBoundingClientRect();
        return event.clientY > rect.top + rect.height / 2;
    }

    #onDragOver(event, element) {
        if (!this.#draggedId) return;
        event.preventDefault();
        const after = this.#dropsAfter(event, element);
        this.#clearDropMarkers();
        element.classList.add(after ? 'is-drop-after' : 'is-drop-before');
    }

    #onDrop(event, element) {
        if (!this.#draggedId) return;
        event.preventDefault();
        const rowId = this.#draggedId;
        const from = this.#indexOf(rowId);
        const over = this.#indexOf(element.dataset.rowId);
        const insertAt = over + (this.#dropsAfter(event, element) ? 1 : 0);
        this.#onDragEnd();
        this.#moveRow(rowId, insertAt > from ? insertAt - 1 : insertAt);
    }

    #onDragEnd() {
        this.#draggedId = null;
        this.$$('.table-row').forEach((row) => {
            row.draggable = false;
            row.classList.remove('is-dragging');
        });
        this.#clearDropMarkers();
    }

    #clearDropMarkers() {
        this.$$('.table-row').forEach((row) => row.classList.remove('is-drop-before', 'is-drop-after'));
    }
}

customElements.define('table-field', TableFieldComponent);
